/// <reference path="./raw.d.ts" />

import arrowSource from "../../../data/towers/arrow.jsonc?raw";
import cannonSource from "../../../data/towers/cannon.jsonc?raw";
import iceTowerSource from "../../../data/towers/ice_tower.jsonc?raw";
import thunderTowerSource from "../../../data/towers/thunder_tower.jsonc?raw";
import magicTowerSource from "../../../data/towers/magic_tower.jsonc?raw";

import gruntSource from "../../../data/enemies/grunt.jsonc?raw";
import runnerSource from "../../../data/enemies/runner.jsonc?raw";
import bruiserSource from "../../../data/enemies/bruiser.jsonc?raw";
import tankSource from "../../../data/enemies/tank.jsonc?raw";
import swarmSource from "../../../data/enemies/swarm.jsonc?raw";
import spiderSource from "../../../data/enemies/spider.jsonc?raw";
import frostGiantSource from "../../../data/enemies/frost_giant.jsonc?raw";
import iceElementalSource from "../../../data/enemies/ice_elemental.jsonc?raw";

import warlordSource from "../../../data/bosses/warlord.jsonc?raw";
import stormDragonSource from "../../../data/bosses/storm_dragon.jsonc?raw";
import frostLordSource from "../../../data/bosses/frost_lord.jsonc?raw";
import shadowKnightSource from "../../../data/bosses/shadow_knight.jsonc?raw";

import frontierEndlessMapSource from "../../../data/maps/frontier_endless.jsonc?raw";

import frontierEndlessWavesFileSource from "../../../data/waves/frontier-endless.jsonc?raw";
import endlessFrontierWavesSource from "../../../data/waves/endless-frontier.jsonc?raw";

import areaDamageSource from "../../../data/skills/area_damage.jsonc?raw";
import slowAllSource from "../../../data/skills/slow_all.jsonc?raw";
import healLivesSource from "../../../data/skills/heal_lives.jsonc?raw";
import attackSpeedBoostSource from "../../../data/skills/attack_speed_boost.jsonc?raw";
import cannonAreaDamageSource from "../../../data/skills/cannon_area_damage.jsonc?raw";
import iceConeSource from "../../../data/skills/ice_cone.jsonc?raw";

import defaultBalanceSource from "../../../data/balance/default.jsonc?raw";
import easyBalanceSource from "../../../data/balance/easy.jsonc?raw";
import normalBalanceSource from "../../../data/balance/normal.jsonc?raw";
import hardBalanceSource from "../../../data/balance/hard.jsonc?raw";
import combatTuningFileSource from "../../../data/combat/default.jsonc?raw";
import achievementsFileSource from "../../../data/achievements/default.jsonc?raw";
import presentationFileSource from "../../../data/presentation/help.jsonc?raw";
import type { GameContent } from "./types";
import { parseJsonc } from "./parseJsonc";
import {
  validateGameContent,
  validateCombatTuning,
  validateAchievementDefinitions,
  validatePresentationConfig,
} from "./validate";

type WaveFile = { items: unknown[] };
type EndlessWaveFile = Record<string, unknown>;

const parse = (source: string, file: string): unknown => parseJsonc(source, file);

function waveItems(file: WaveFile, source: string): Array<{ id: string; [key: string]: unknown }> {
  return file.items.map((item, index) => {
    if (
      typeof item !== "object" ||
      item === null ||
      Array.isArray(item) ||
      typeof (item as { id?: unknown }).id !== "string"
    ) {
      throw new Error(`Invalid wave item at ${source}.items[${index}]`);
    }
    return item as { id: string; [key: string]: unknown };
  });
}
const arrow = parse(arrowSource, "data/towers/arrow.jsonc");
const cannon = parse(cannonSource, "data/towers/cannon.jsonc");
const iceTower = parse(iceTowerSource, "data/towers/ice_tower.jsonc");
const thunderTower = parse(thunderTowerSource, "data/towers/thunder_tower.jsonc");
const magicTower = parse(magicTowerSource, "data/towers/magic_tower.jsonc");
const grunt = parse(gruntSource, "data/enemies/grunt.jsonc");
const runner = parse(runnerSource, "data/enemies/runner.jsonc");
const bruiser = parse(bruiserSource, "data/enemies/bruiser.jsonc");
const tank = parse(tankSource, "data/enemies/tank.jsonc");
const swarm = parse(swarmSource, "data/enemies/swarm.jsonc");
const spider = parse(spiderSource, "data/enemies/spider.jsonc");
const frostGiant = parse(frostGiantSource, "data/enemies/frost_giant.jsonc");
const iceElemental = parse(iceElementalSource, "data/enemies/ice_elemental.jsonc");
const warlord = parse(warlordSource, "data/bosses/warlord.jsonc");
const stormDragon = parse(stormDragonSource, "data/bosses/storm_dragon.jsonc");
const frostLord = parse(frostLordSource, "data/bosses/frost_lord.jsonc");
const shadowKnight = parse(shadowKnightSource, "data/bosses/shadow_knight.jsonc");
const frontierEndlessMap = parse(frontierEndlessMapSource, "data/maps/frontier_endless.jsonc");
const frontierEndlessWavesFile = parse(
  frontierEndlessWavesFileSource,
  "data/waves/frontier-endless.jsonc",
) as WaveFile;
const endlessFrontierWavesFile = parse(
  endlessFrontierWavesSource,
  "data/waves/endless-frontier.jsonc",
) as EndlessWaveFile;
const areaDamage = parse(areaDamageSource, "data/skills/area_damage.jsonc");
const slowAll = parse(slowAllSource, "data/skills/slow_all.jsonc");
const healLives = parse(healLivesSource, "data/skills/heal_lives.jsonc");
const attackSpeedBoost = parse(attackSpeedBoostSource, "data/skills/attack_speed_boost.jsonc");
const cannonAreaDamage = parse(cannonAreaDamageSource, "data/skills/cannon_area_damage.jsonc");
const iceCone = parse(iceConeSource, "data/skills/ice_cone.jsonc");
const defaultBalance = parse(defaultBalanceSource, "data/balance/default.jsonc");
const easyBalance = parse(easyBalanceSource, "data/balance/easy.jsonc");
const normalBalance = parse(normalBalanceSource, "data/balance/normal.jsonc");
const hardBalance = parse(hardBalanceSource, "data/balance/hard.jsonc");
const combatTuningFile = parse(combatTuningFileSource, "data/combat/default.jsonc");
const achievementsFile = parse(achievementsFileSource, "data/achievements/default.jsonc");
const presentationFile = parse(presentationFileSource, "data/presentation/help.jsonc");

const towers = {
  arrow,
  cannon,
  ice_tower: iceTower,
  thunder_tower: thunderTower,
  magic_tower: magicTower,
};

const enemies = {
  grunt,
  runner,
  bruiser,
  tank,
  swarm,
  spider,
  frost_giant: frostGiant,
  ice_elemental: iceElemental,
};

const bosses = {
  warlord,
  storm_dragon: stormDragon,
  frost_lord: frostLord,
  shadow_knight: shadowKnight,
};

const maps = {
  frontier_endless: frontierEndlessMap,
};

const skills = {
  area_damage: areaDamage,
  slow_all: slowAll,
  heal_lives: healLives,
  attack_speed_boost: attackSpeedBoost,
  cannon_area_damage: cannonAreaDamage,
  ice_cone: iceCone,
};

const waves = Object.fromEntries(
  waveItems(frontierEndlessWavesFile, "data/waves/frontier-endless.jsonc").map((wave) => [
    wave.id,
    wave,
  ]),
);

const rawContent = {
  towers,
  enemies,
  bosses,
  waves,
  maps,
  endlessWaves: {
    frontier_endless: endlessFrontierWavesFile,
  },
  skills,
  balance: defaultBalance,
  difficultyBalances: {
    easy: easyBalance,
    normal: normalBalance,
    hard: hardBalance,
  },
  combatTuning: validateCombatTuning(combatTuningFile),
  achievements: validateAchievementDefinitions(achievementsFile, "achievements"),
  presentation: validatePresentationConfig(presentationFile),
};

/** 组装并验证各运行时共享的规范内容。 */
export function loadGameContent(): GameContent {
  return validateGameContent(rawContent);
}

export const gameContent: GameContent = loadGameContent();
